import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import PageContainer from '../../components/layout/PageContainer';
import { useAuth } from '../../hooks/useAuth';
import ManagerOverview from './ManagerOverview';
import ManagerTicketsView from './ManagerTicketsView';
import ManagerContractsView from './ManagerContractsView';
import ManagerTeamLeadsView from './ManagerTeamLeadsView';
import ManagerEngineersView from './ManagerEngineersView';
import ManagerSitesView from './ManagerSitesView';
import ManagerSpocsView from './ManagerSpocsView';
import ManagerReportsView from './ManagerReportsView';
import ManagerSettingsView from './ManagerSettingsView';
import ManagerCompanyView from './ManagerCompanyView';

const ManagerDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const pages = [
    { id: 'dashboard', name: 'Dashboard', icon: 'dashboard' },
    { id: 'tickets', name: 'Tickets', icon: 'tickets' },
    { id: 'contracts', name: 'AMC Contracts', icon: 'contracts' },
    { id: 'engineers', name: 'Engineers', icon: 'engineers' },
    { id: 'teamleads', name: 'Team Leads', icon: 'teamleads' },
    { id: 'spocs', name: 'SPOCs', icon: 'spocs' },
    { id: 'sites', name: 'Sites', icon: 'sites' },
    { id: 'company', name: 'Customer', icon: 'customer' },
    { id: 'reports', name: 'Reports', icon: 'reports' },
    { id: 'settings', name: 'Settings', icon: 'settings' }
  ];

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'var(--background)'
  };

  const pageSegment = location.pathname.split('/')[2] || 'dashboard';
  const currentPage = pages.some((page) => page.id === pageSegment) ? pageSegment : 'dashboard';

  const setCurrentPage = (pageId) => {
    navigate(`/manager/${pageId}`);
  };

  const navigateToTickets = (statusFilter = '', priorityFilter = '', activeOnly = false) => {
    if (priorityFilter) {
      navigate(`/manager/tickets?priority=${priorityFilter}${activeOnly ? '&activeOnly=true' : ''}`);
    } else if (statusFilter === 'OVERDUE'){
      navigate(`/manager/tickets?overdue=true`);
    }else if (statusFilter === 'CLOSED') {
      navigate(`/manager/tickets?status=CLOSED&closedToday=true`);
    } else if (statusFilter) {
      navigate(`/manager/tickets?status=${statusFilter}`);
    } else {
      navigate('/manager/tickets');
    }
  };

  const navigateToEngineers = (statusFilter = '') => {
    if (statusFilter) {
      navigate(`/manager/engineers?status=${statusFilter}`);
    } else {
      navigate('/manager/engineers');
    }
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: sidebarOpen ? '260px' : '80px',
    transition: 'margin-left 0.3s ease',
    minHeight: '100vh'
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <ManagerOverview navigateToTickets={navigateToTickets} navigateToEngineers={navigateToEngineers} setCurrentPage={setCurrentPage}/>;
      case 'tickets':
        return <ManagerTicketsView />;
      case 'contracts':
        return <ManagerContractsView />;
      case 'teamleads':
        return <ManagerTeamLeadsView />;
      case 'engineers':
        return <ManagerEngineersView />;
      case 'sites':
        return <ManagerSitesView />;
      case 'spocs':
        return <ManagerSpocsView />;
      case 'company':
        return <ManagerCompanyView />;
      case 'reports':
        return <ManagerReportsView />;
      case 'settings':
        return <ManagerSettingsView />;
      default:
        return <ManagerOverview />;
    }
  };

  const getPageTitle = () => {
    const page = pages.find(p => p.id === currentPage);
    return page?.name || 'Dashboard';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      dashboard: 'Overview of your team performance',
      tickets: 'View and track service tickets',
      contracts: 'View AMC contracts',
      teamleads: 'View teamleads and statistics',
      engineers: 'View field engineers and assignments',
      spocs: 'View customer spocs and their assignments',
      sites: 'View customer sites',
      company: 'View customer details',
      reports: 'Analytics and performance reports',
      settings: 'Profile and preferences'
    };
    return subtitles[currentPage] || '';
  };

  return (
    <div style={containerStyle}>
      <Sidebar
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pages={pages}
        onLogout={logout}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div style={mainContentStyle}>
        <PageContainer
          title={getPageTitle()}
          subtitle={getPageSubtitle()}
        >
          {renderPage()}
        </PageContainer>
      </div>
    </div>
  );
};

export default ManagerDashboard;
